import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as THREE from 'three';
import store from '../redux/store';
import { setPushIntersectedObject } from '../redux/Slice/gridSlice';
import {
  applyLightVisibility,
  shouldShowLightHelper,
  syncAllLightHelpersVisibility,
  syncLightHelperFromLight,
  syncLightIconFromLight,
  LIGHT_ICON_SUFFIX,
} from '../utils/ThreeJsUtils/lightHelperUtils';
import {
  configureLightShadow,
  disableLightHelperShadows,
  disableAllSceneLightHelperShadows,
  refreshAllLightShadows,
  refreshRendererShadowMap,
  REFRESH_SCENE_SHADOWS_EVENT,
  SCENE_LIGHTS_CLEARED_EVENT,
} from '../utils/ThreeJsUtils/lightShadowUtils';
import Light1Image from '../assets/images/icons/Light1.svg';
import Light2Image from '../assets/images/icons/Light2.svg';
import Light3Image from '../assets/images/icons/Light3.svg';
import ambientlight from '../assets/images/icons/ambientlight.svg';
import arealight from '../assets/images/icons/arealight.svg';

const LIGHT_ICON_SCALE = 0.65;

const LIGHT_ICON_BY_TYPE = {
  'Directional Light': Light2Image,
  'Point Light': Light1Image,
  'Spot Light': Light3Image,
  'Ambient Light': ambientlight,
  'Area Light': arealight,
};

const useLightHook = ({
  scene,
  directionalLightRef,
  renderer,
  window_type,
  selectedObject,
  camera,
  transformControls,
}) => {
  const { light_data } = useSelector((state) => state.assets_object);
  const lightsRef = useRef({});
  const iconTextureLoaderRef = useRef(null);
  const iconTextureCacheRef = useRef({});

  const getLightIconTexture = (lightType) => {
    const iconPath = LIGHT_ICON_BY_TYPE[lightType];
    if (!iconPath) return null;

    if (!iconTextureLoaderRef.current) {
      iconTextureLoaderRef.current = new THREE.TextureLoader();
    }

    if (iconTextureCacheRef.current[iconPath]) {
      return iconTextureCacheRef.current[iconPath];
    }

    const texture = iconTextureLoaderRef.current.load(iconPath);
    if ('colorSpace' in texture && THREE.SRGBColorSpace) {
      texture.colorSpace = THREE.SRGBColorSpace;
    }
    iconTextureCacheRef.current[iconPath] = texture;
    return texture;
  };

  // Helper function to add light to proper parent
  const addLightToParent = (light, threeLight) => {
    if (light.parent) {
      const parentObj = scene.getObjectByName(light.parent);
      if (parentObj) {
        parentObj.add(threeLight);
      } else {
        scene.add(threeLight);
      }
    } else {
      scene.add(threeLight);
    }
  };

  const configureShadow = (threeLight, light, lightType) => {
    configureLightShadow(threeLight, light, lightType, renderer);
  };

  // Update light transform (position, rotation, scale)
  const updateLightTransform = (threeLight, light) => {
    const { position = {}, rotation = {}, scale = {} } = light;

    if (Object.keys(position).length) {
      threeLight.position.set(
        position.x ?? threeLight.position.x,
        position.y ?? threeLight.position.y,
        position.z ?? threeLight.position.z
      );
    }

    if (Object.keys(rotation).length) {
      threeLight.rotation.set(
        rotation.x ?? threeLight.rotation.x,
        rotation.y ?? threeLight.rotation.y,
        rotation.z ?? threeLight.rotation.z
      );
    }

    // if (Object.keys(scale).length) {
    //   threeLight.scale.set(
    //     scale.x ?? threeLight.scale.x,
    //     scale.y ?? threeLight.scale.y,
    //     scale.z ?? threeLight.scale.z
    //   );
    // }

    threeLight.updateMatrixWorld();
  };

  // Sync helper + icon while dragging in design mode
  const updateEditorVisualsImmediately = (threeLight, lightObj) => {
    if (window_type !== 'design' || !threeLight || !lightObj) return;
    if (lightObj.helper) {
      syncLightHelperFromLight(threeLight, lightObj.helper);
    }
    if (lightObj.icon) {
      syncLightIconFromLight(threeLight, lightObj.icon);
    }
  };

  const updateLightIconTransform = (threeLight, icon) => {
    syncLightIconFromLight(threeLight, icon);
  };

  const manageLightIcon = (light, threeLight, lightType, existingIcon) => {
    if (window_type !== 'design') {
      if (existingIcon?.parent) {
        existingIcon.parent.remove(existingIcon);
      }
      const orphanedIcon = scene.getObjectByName(light.id + LIGHT_ICON_SUFFIX);
      if (orphanedIcon?.parent) {
        orphanedIcon.parent.remove(orphanedIcon);
      }
      return null;
    }

    const iconTexture = getLightIconTexture(lightType);
    if (!iconTexture) return null;

    if (existingIcon && !scene.getObjectByName(`${light.id}${LIGHT_ICON_SUFFIX}`)) {
      existingIcon = null;
    }

    if (!existingIcon) {
      const material = new THREE.SpriteMaterial({
        map: iconTexture,
        transparent: true,
        depthWrite: false,
        depthTest: false,
      });
      const icon = new THREE.Sprite(material);
      icon.name = `${light.id}${LIGHT_ICON_SUFFIX}`;
      icon.renderOrder = 999;
      icon.scale.set(LIGHT_ICON_SCALE, LIGHT_ICON_SCALE, 1);
      icon.userData.isEditorLightIcon = true;
      icon.userData.lightId = light.id;
      updateLightIconTransform(threeLight, icon);
      icon.visible = shouldShowLightHelper(light);
      scene.add(icon);
      store.dispatch(setPushIntersectedObject(icon));
      return icon;
    }

    existingIcon.material.map = iconTexture;
    existingIcon.scale.set(LIGHT_ICON_SCALE, LIGHT_ICON_SCALE, 1);
    existingIcon.visible = shouldShowLightHelper(light);
    updateLightIconTransform(threeLight, existingIcon);
    return existingIcon;
  };

  // Create or update helper for a light
  const manageHelper = (light, threeLight, lightType, existingHelper) => {
    if (window_type !== 'design') {
      // Remove helper if not in design mode
      if (existingHelper) {
        scene.remove(existingHelper);
        return null;
      }

      // Also remove orphaned helpers
      const orphanedHelper = scene.getObjectByName(light.id + '-helper');
      if (orphanedHelper) {
        scene.remove(orphanedHelper);
      }
      return null;
    }

    if (
      existingHelper &&
      !scene.getObjectByName(`${light.id}-helper`)
    ) {
      existingHelper = null;
    }

    // Create helper if it doesn't exist
    if (!existingHelper) {
      let newHelper = null;

      switch (lightType) {
        case 'Directional Light':
          newHelper = new THREE.DirectionalLightHelper(
            threeLight,
            3,
            light.color || '#ffffff'
          );
          break;
        case 'Point Light':
          newHelper = new THREE.PointLightHelper(
            threeLight,
            1,
            light.color || '#ffffff'
          );
          break;
        case 'Spot Light':
          newHelper = new THREE.SpotLightHelper(
            threeLight,
            light.color || '#ffffff'
          );
          break;
        case 'Area Light': {
          // custom mesh plane helper so we can control transparency and
          // participate in transform controls properly
          const width = light.width !== undefined ? light.width : 50;
          const height = light.height !== undefined ? light.height : 50;
          const geom = new THREE.PlaneGeometry(width, height);
          const mat = new THREE.MeshBasicMaterial({
            color: light.color || '#ffffff',
            transparent: true,
            wireframe: true,
            opacity: 0.2, // mostly see-through
            side: THREE.DoubleSide,
            depthWrite: false,
            depthTest: false, // avoid z-fighting with grid
          });
          newHelper = new THREE.Mesh(geom, mat);
          newHelper.type = 'AreaLightHelper'; // custom type for filtering
          // store dims for later updates
          newHelper.userData.width = width;
          newHelper.userData.height = height;
          break;
        }
        case 'Ambient Light': {
          const ambientGeom = new THREE.SphereGeometry(0.5, 10, 10);
          const ambientMat = new THREE.MeshBasicMaterial({
            color: light.color || '#ffffff',
            transparent: true,
            wireframe: true,
            opacity: 0.35,
            depthWrite: false,
            depthTest: false,
          });
          newHelper = new THREE.Mesh(ambientGeom, ambientMat);
          newHelper.type = 'AmbientLightHelper';
          break;
        }
        default:
          return null;
      }

      if (newHelper) {
        newHelper.name = light.id + '-helper';
        newHelper.visible = shouldShowLightHelper(light);
        disableLightHelperShadows(newHelper);
        newHelper.position.set(
          light?.position?.x || 0,
          light?.position?.y || 10,
          light?.position?.z || 0
        );
        scene.add(newHelper);
        store.dispatch(setPushIntersectedObject(newHelper));
        return newHelper;
      }
    }

    // Update existing helper
    if (existingHelper) {
      disableLightHelperShadows(existingHelper);
      if (lightType === 'Area Light') {
        // adjust plane dimensions and color
        const newW = light.width !== undefined ? light.width : 50;
        const newH = light.height !== undefined ? light.height : 50;
        if (
          existingHelper.userData.width !== newW ||
          existingHelper.userData.height !== newH
        ) {
          existingHelper.geometry.dispose();
          existingHelper.geometry = new THREE.PlaneGeometry(newW, newH);
          existingHelper.userData.width = newW;
          existingHelper.userData.height = newH;
        }
        if (existingHelper.material) {
          existingHelper.material.color.set(light.color || '#ffffff');
        }
      } else if (lightType === 'Ambient Light' && existingHelper.material) {
        existingHelper.material.color.set(light.color || '#ffffff');
      } else if (typeof existingHelper.update === 'function') {
        existingHelper.update();
      }
    }

    return existingHelper;
  };

  // Create Directional Light
  const createDirectionalLight = (light) => {
    const threeLight = new THREE.DirectionalLight(
      light.color || 0xffffff,
      light.intensity ?? 1
    );
    threeLight.name = light.id;
    threeLight.deleteType = 'Light';

    // Set default position if not provided
    if (!light.position || Object.keys(light.position).length === 0) {
      threeLight.position.set(0, 10, 0);
    }

    addLightToParent(light, threeLight);
    updateLightTransform(threeLight, light);
    configureShadow(threeLight, light, 'Directional Light');

    // Store reference
    if (directionalLightRef) {
      directionalLightRef.current = threeLight;
    }

    store.dispatch(setPushIntersectedObject(threeLight));

    const helper = manageHelper(light, threeLight, 'Directional Light', null);
    const icon = manageLightIcon(light, threeLight, 'Directional Light', null);

    return { light: threeLight, helper, icon, type: 'Directional Light' };
  };

  // Update Directional Light
  const updateDirectionalLight = (lightObj, light) => {
    const threeLight = lightObj.light;

    threeLight.color.set(light.color || 0xffffff);
    threeLight.intensity = light.intensity ?? 1;

    updateLightTransform(threeLight, light);
    configureShadow(threeLight, light, 'Directional Light');

    const helper = manageHelper(
      light,
      threeLight,
      'Directional Light',
      lightObj.helper
    );
    const icon = manageLightIcon(
      light,
      threeLight,
      'Directional Light',
      lightObj.icon
    );

    return { light: threeLight, helper, icon, type: 'Directional Light' };
  };

  // Create Ambient Light
  const createAmbientLight = (light) => {
    const threeLight = new THREE.AmbientLight(
      light.color || 0xffffff,
      light.intensity ?? 0.3
    );
    threeLight.name = light.id;
    threeLight.deleteType = 'Light';

    if (!light.position || Object.keys(light.position).length === 0) {
      threeLight.position.set(0, 10, 0);
    }

    addLightToParent(light, threeLight);
    updateLightTransform(threeLight, light);

    store.dispatch(setPushIntersectedObject(threeLight));

    const helper = manageHelper(light, threeLight, 'Ambient Light', null);
    const icon = manageLightIcon(light, threeLight, 'Ambient Light', null);
    return { light: threeLight, helper, icon, type: 'Ambient Light' };
  };

  // Update Ambient Light
  const updateAmbientLight = (lightObj, light) => {
    const threeLight = lightObj.light;

    threeLight.color.set(light.color || 0xffffff);
    threeLight.intensity = light.intensity ?? 0.3;

    updateLightTransform(threeLight, light);

    const helper = manageHelper(
      light,
      threeLight,
      'Ambient Light',
      lightObj.helper
    );
    const icon = manageLightIcon(
      light,
      threeLight,
      'Ambient Light',
      lightObj.icon
    );
    return { light: threeLight, helper, icon, type: 'Ambient Light' };
  };

  // Create Point Light
  const createPointLight = (light) => {
    const threeLight = new THREE.PointLight(
      light.color || 0xffffff,
      light.intensity ?? 20,
      light.range !== undefined ? (light.range === 0 ? 0.001 : light.range) : 100
    );
    // Keep point/spot intensity behavior consistent with directional (no distance falloff by default).
    threeLight.decay = light.decay !== undefined ? light.decay : 0;
    threeLight.name = light.id;

    // Set position with defaults
    threeLight.position.set(
      light.position?.x ?? 0,
      light.position?.y ?? 10,
      light.position?.z ?? 0
    );

    addLightToParent(light, threeLight);
    configureShadow(threeLight, light, 'Point Light');

    const helper = manageHelper(light, threeLight, 'Point Light', null);
    const icon = manageLightIcon(light, threeLight, 'Point Light', null);

    return { light: threeLight, helper, icon, type: 'Point Light' };
  };

  // Update Point Light
  const updatePointLight = (lightObj, light) => {
    const threeLight = lightObj.light;

    threeLight.color.set(light.color || 0xffffff);
    threeLight.intensity = light.intensity ?? 20;
    threeLight.distance = light.range !== undefined ? (light.range === 0 ? 0.001 : light.range) : 100;
    threeLight.decay = light.decay !== undefined ? light.decay : 0;

    threeLight.position.set(
      light.position?.x ?? 0,
      light.position?.y ?? 10,
      light.position?.z ?? 0
    );

    configureShadow(threeLight, light, 'Point Light');

    const helper = manageHelper(
      light,
      threeLight,
      'Point Light',
      lightObj.helper
    );
    const icon = manageLightIcon(light, threeLight, 'Point Light', lightObj.icon);

    return { light: threeLight, helper, icon, type: 'Point Light' };
  };

  // Create Spot Light
  const createSpotLight = (light) => {
    const threeLight = new THREE.SpotLight(
      light.color || 0xffffff,
      light.intensity ?? 20,
      light.range !== undefined ? (light.range === 0 ? 0.001 : light.range) : 100,
      light.angle !== undefined ? light.angle : Math.PI / 3
    );
    // Keep point/spot intensity behavior consistent with directional (no distance falloff by default).
    threeLight.decay = light.decay !== undefined ? light.decay : 0;
    threeLight.name = light.id;

    // Set position with defaults
    threeLight.position.set(
      light.position?.x ?? 0,
      light.position?.y ?? 10,
      light.position?.z ?? 0
    );

    // Setup target and orientation.
    // If an explicit rotation is provided, attach the target as a child so
    // rotating the light (via TransformControls) will rotate its direction.
    if (light.rotation && Object.keys(light.rotation).length > 0) {
      threeLight.rotation.set(
        light.rotation.x ?? 0,
        light.rotation.y ?? 0,
        light.rotation.z ?? 0
      );

      // Place target directly in front of the light in local space so rotation affects it
      threeLight.add(threeLight.target);
      threeLight.target.position.set(0, -1, 0);
    } else {
      // No rotation provided: use absolute target position (defaults to origin)
      const targetPos = light.target || { x: 0, y: 0, z: 0 };
      threeLight.target.position.set(targetPos.x, targetPos.y, targetPos.z);

      // Add target to parent or scene
      if (light.parent) {
        const parentObj = scene.getObjectByName(light.parent);
        if (parentObj) {
          parentObj.add(threeLight.target);
        } else {
          scene.add(threeLight.target);
        }
      } else {
        scene.add(threeLight.target);
      }
    }

    addLightToParent(light, threeLight);
    configureShadow(threeLight, light, 'Spot Light');

    const helper = manageHelper(light, threeLight, 'Spot Light', null);
    const icon = manageLightIcon(light, threeLight, 'Spot Light', null);

    return { light: threeLight, helper, icon, type: 'Spot Light' };
  };

  // Update Spot Light
  const updateSpotLight = (lightObj, light) => {
    const threeLight = lightObj.light;

    threeLight.color.set(light.color || 0xffffff);
    threeLight.intensity = light.intensity ?? 20;
    threeLight.distance = light.range !== undefined ? (light.range === 0 ? 0.001 : light.range) : 100;
    threeLight.decay = light.decay !== undefined ? light.decay : 0;
    threeLight.angle = light.angle !== undefined ? light.angle : Math.PI / 3;

    threeLight.position.set(
      light.position?.x ?? 0,
      light.position?.y ?? 10,
      light.position?.z ?? 0
    );

    // Update orientation/target.
    if (light.rotation && Object.keys(light.rotation).length > 0) {
      // If rotation is provided, apply it and ensure the target is attached
      threeLight.rotation.set(
        light.rotation.x ?? threeLight.rotation.x,
        light.rotation.y ?? threeLight.rotation.y,
        light.rotation.z ?? threeLight.rotation.z
      );

      // If the target is not a child of the light, re-parent it so rotation controls direction
      if (threeLight.target.parent !== threeLight) {
        if (threeLight.target.parent) {
          threeLight.target.parent.remove(threeLight.target);
        }
        threeLight.add(threeLight.target);
      }

      // Place the target slightly in front in local space
      threeLight.target.position.set(0, -1, 0);
    } else {
      // No rotation: use absolute target position (defaults to origin)
      const targetPos = light.target || { x: 0, y: 0, z: 0 };

      // If the target was previously a child of the light, detach it
      if (threeLight.target.parent === threeLight) {
        threeLight.remove(threeLight.target);
      }

      threeLight.target.position.set(targetPos.x, targetPos.y, targetPos.z);

      // Add target to parent or scene
      if (light.parent) {
        const parentObj = scene.getObjectByName(light.parent);
        if (parentObj) {
          parentObj.add(threeLight.target);
        } else {
          scene.add(threeLight.target);
        }
      } else {
        scene.add(threeLight.target);
      }
    }

    configureShadow(threeLight, light, 'Spot Light');

    const helper = manageHelper(
      light,
      threeLight,
      'Spot Light',
      lightObj.helper
    );
    const icon = manageLightIcon(light, threeLight, 'Spot Light', lightObj.icon);

    return { light: threeLight, helper, icon, type: 'Spot Light' };
  };

  // Create Area Light
  const createAreaLight = (light) => {
    const threeLight = new THREE.RectAreaLight(
      light.color || 0xffffff,
      light.intensity ?? 10,
      light.width !== undefined ? light.width : 50,
      light.height !== undefined ? light.height : 50
    );
    threeLight.name = light.id;

    // Set position with defaults
    threeLight.position.set(
      light.position?.x ?? 0,
      light.position?.y ?? 10,
      light.position?.z ?? 0
    );

    // Apply rotation if provided, otherwise look at origin
    if (light.rotation && Object.keys(light.rotation).length > 0) {
      threeLight.rotation.set(
        light.rotation.x ?? 0,
        light.rotation.y ?? 0,
        light.rotation.z ?? 0
      );
    } else {
      threeLight.lookAt(0, 0, 0);
    }

    addLightToParent(light, threeLight);

    // configure shadow for area light
    configureShadow(threeLight, light, 'Area Light');

    // create helper for area light
    const helper = manageHelper(light, threeLight, 'Area Light', null);
    // attach helper reference directly so other logic (transform events) can find it
    if (helper) threeLight.helper = helper;
    const icon = manageLightIcon(light, threeLight, 'Area Light', null);
    return { light: threeLight, helper, icon, type: 'Area Light' };
  };

  // Update Area Light
  const updateAreaLight = (lightObj, light) => {
    const threeLight = lightObj.light;

    threeLight.color.set(light.color || 0xffffff);
    threeLight.intensity = light.intensity ?? 10;

    // Update dimensions
    threeLight.width = light.width !== undefined ? light.width : 50;
    threeLight.height = light.height !== undefined ? light.height : 50;

    threeLight.position.set(
      light.position?.x ?? 0,
      light.position?.y ?? 10,
      light.position?.z ?? 0
    );

    // Apply rotation if provided
    if (light.rotation && Object.keys(light.rotation).length > 0) {
      threeLight.rotation.set(
        light.rotation.x ?? 0,
        light.rotation.y ?? 0,
        light.rotation.z ?? 0
      );
    }

    // configure shadow for area light
    configureShadow(threeLight, light, 'Area Light');

    // update helper if exists
    const helper = manageHelper(
      light,
      threeLight,
      'Area Light',
      lightObj.helper
    );
    if (helper) threeLight.helper = helper;
    const icon = manageLightIcon(light, threeLight, 'Area Light', lightObj.icon);

    return { light: threeLight, helper, icon, type: 'Area Light' };
  };

  useEffect(() => {
    const onLightsCleared = () => {
      lightsRef.current = {};
    };
    const onRefreshShadows = () => {
      if (scene && renderer && Array.isArray(light_data)) {
        refreshAllLightShadows(scene, renderer, light_data);
      }
    };

    globalThis.addEventListener(SCENE_LIGHTS_CLEARED_EVENT, onLightsCleared);
    globalThis.addEventListener(REFRESH_SCENE_SHADOWS_EVENT, onRefreshShadows);

    return () => {
      globalThis.removeEventListener(SCENE_LIGHTS_CLEARED_EVENT, onLightsCleared);
      globalThis.removeEventListener(
        REFRESH_SCENE_SHADOWS_EVENT,
        onRefreshShadows
      );
    };
  }, [scene, renderer, light_data]);

  useEffect(() => {
    if (!scene || !renderer || !Array.isArray(light_data)) {
      return;
    }

    // Cleanup: Remove lights that are no longer in the array
    Object.keys(lightsRef.current).forEach((id) => {
      if (!light_data.find((l) => l.id === id)) {
        const lightObj = lightsRef.current[id];

        // Remove light
        if (lightObj?.light) {
          // Remove target for spot lights
          if (lightObj.type === 'Spot Light' && lightObj.light.target) {
            scene.remove(lightObj.light.target);
          }
          scene.remove(lightObj.light);
        }

        // Remove helper
        if (lightObj?.helper) {
          scene.remove(lightObj.helper);
        }
        if (lightObj?.icon) {
          scene.remove(lightObj.icon);
        }

        delete lightsRef.current[id];
      }
    });

    // Process each light in the array
    light_data.forEach((light) => {
      let lightObj = lightsRef.current[light.id];

      if (lightObj?.helper && !scene.getObjectByName(`${light.id}-helper`)) {
        lightObj.helper = null;
      }

      // Safety check: ensure the light object is actually in the scene
      if (lightObj && lightObj.light && !scene.children.includes(lightObj.light)) {
        console.log(`[useLightHook] Light ${light.id} found in ref but not in scene. Re-adding.`);
        scene.add(lightObj.light);
      }
      let newLightObj = null;
      let preservedChildren = []; // Store children to preserve hierarchy

      // Check if light type changed - if so, remove old and create new
      if (lightObj && lightObj.type !== light.light_type) {
        // Preserve children before removing the old light
        if (lightObj.light && lightObj.light.children.length > 0) {
          // Save all children (excluding helper) with their world transforms
          const childrenToPreserve = lightObj.light.children.filter((child) => {
            // Exclude helper objects
            return (
              !child.name?.includes('-helper') && child !== lightObj.helper
            );
          });

          // Store children with their world transforms before removal
          preservedChildren = childrenToPreserve.map((child) => {
            // Store world transform before removal
            const worldPosition = child.getWorldPosition(new THREE.Vector3());
            const worldQuaternion = child.getWorldQuaternion(
              new THREE.Quaternion()
            );
            const worldScale = child.getWorldScale(new THREE.Vector3());

            // Remove child from light before removing the light itself
            // This prevents children from being removed when the light is removed
            lightObj.light.remove(child);
            // Temporarily add to scene to keep it alive
            scene.add(child);

            return {
              object: child,
              worldPosition,
              worldQuaternion,
              worldScale,
            };
          });
        }

        // Remove old light and helper
        if (lightObj.light) {
          if (lightObj.type === 'Spot Light' && lightObj.light.target) {
            scene.remove(lightObj.light.target);
          }
          scene.remove(lightObj.light);
        }
        if (lightObj.helper) {
          scene.remove(lightObj.helper);
        }
        if (lightObj.icon) {
          scene.remove(lightObj.icon);
        }
        delete lightsRef.current[light.id];
        lightObj = null;
      }

      // Create or update light based on type
      switch (light.light_type) {
        case 'Directional Light':
          newLightObj = lightObj
            ? updateDirectionalLight(lightObj, light)
            : createDirectionalLight(light);
          break;

        case 'Ambient Light':
          newLightObj = lightObj
            ? updateAmbientLight(lightObj, light)
            : createAmbientLight(light);
          break;

        case 'Point Light':
          newLightObj = lightObj
            ? updatePointLight(lightObj, light)
            : createPointLight(light);
          break;

        case 'Spot Light':
          newLightObj = lightObj
            ? updateSpotLight(lightObj, light)
            : createSpotLight(light);
          break;

        case 'Area Light':
          newLightObj = lightObj
            ? updateAreaLight(lightObj, light)
            : createAreaLight(light);
          break;

        default:
          break;
      }

      // Immediately update helpers to reflect current light transform
      if (newLightObj && newLightObj.light) {
        updateEditorVisualsImmediately(newLightObj.light, newLightObj);
        if (newLightObj.icon) {
          updateLightIconTransform(newLightObj.light, newLightObj.icon);
        }
      }

      // Reattach preserved children to the new light
      if (newLightObj && newLightObj.light && preservedChildren.length > 0) {
        preservedChildren.forEach(
          ({ object, worldPosition, worldQuaternion, worldScale }) => {
            // Remove from scene first
            scene.remove(object);

            // Attach child to new light (preserves world transform)
            newLightObj.light.attach(object);

            // Verify and restore world transform if needed
            // attach() should preserve world transform, but we verify
            const currentWorldPos = object.getWorldPosition(
              new THREE.Vector3()
            );
            const posDiff = currentWorldPos.distanceTo(worldPosition);

            // If transform changed significantly, restore it
            if (posDiff > 0.001) {
              object.position.copy(worldPosition);
              object.quaternion.copy(worldQuaternion);
              // object.scale.copy(worldScale);
              object.updateMatrixWorld();
            }
          }
        );
      }

      if (newLightObj?.light) {
        applyLightVisibility(newLightObj.light, light, scene, {
          isDesignMode: window_type === 'design',
          helper: newLightObj.helper,
        });
      }

      // Store the light object reference
      if (newLightObj) {
        lightsRef.current[light.id] = newLightObj;
      }
    });

    if (window_type === 'design') {
      syncAllLightHelpersVisibility(scene, light_data);
      disableAllSceneLightHelperShadows(scene);
    }

    refreshAllLightShadows(scene, renderer, light_data);
    refreshRendererShadowMap(renderer);
  }, [
    light_data,
    scene,
    renderer,
    window_type,
    directionalLightRef,
  ]);

  return null;
};

export default useLightHook;
